import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { ThemeContext } from "../../pages/qcast-logo";
import { IoArrowBack, IoArrowForward, IoPlay, IoStop } from "react-icons/io5";
import QcastApi from "../../api/api-qcast";
import { useSelector } from "react-redux";
import { BigPlayButton, ControlBar, Player } from "video-react";
import { useDrag } from "@use-gesture/react";

const PlayResponse = (props) => {
  const theme = useContext(ThemeContext);
  const { bg } = theme;

  const { userId, token } = useSelector((state) => state.auth);
  const { currentResponse } = useSelector((state) => state.qcast);
  const [responseData, setResponseData] = useState(null);
  const [currentVidIndex, setCurrentVidIndex] = useState(0);
  const [ansVidInfo, setAnsVidInfo] = useState({});
  const [quesVidInfo, setQuesVidInfo] = useState({});

  const ansVidRef = useRef(null);
  const quesVidRef = useRef(null);
  const { getResponseDeepCopy } = QcastApi();

  const [isPlaying, setIsPlaying] = useState(false);

  const [scrubWidth, setScrubWidth] = useState({
    isDragging: false,
    width: 0,
  });
  const [duration, setDuration] = useState(null);

  const scrubCircle = useRef(null);

  const bindTimelineHandler = useDrag(
    (state) => {
      if (!state.active || duration === null) {
        setScrubWidth((prev) => ({ ...prev, isDragging: false }));
        return;
      }

      let per =
        ((scrubCircle.current.offsetLeft + state._delta[0]) / 188) * 100;
      let div = duration / 100;
      let seekingTime = parseInt((per * div).toFixed(2));
      ansVidRef.current.seek(seekingTime);

      let ansIndexes = responseData.answerIndex
        ? responseData.answerIndex.split(";")
        : [];
      ansIndexes.unshift(0);

      if (
        seekingTime >= ansIndexes[currentVidIndex + 1] / 1000 &&
        state.direction[0] === 1 &&
        ansIndexes.length - 1 !== currentVidIndex
      ) {
        setCurrentVidIndex(currentVidIndex + 1);
      } else if (
        seekingTime <= ansIndexes[currentVidIndex] / 1000 &&
        state.direction[0] === -1 &&
        currentVidIndex > 0
      ) {
        setCurrentVidIndex(currentVidIndex - 1);
      }

      if (currentVidIndex > 0) {
        let sec = ansIndexes[currentVidIndex] / 1000;
        const seekingTimeQ = seekingTime - sec;
        if (seekingTime >= sec) {
          quesVidRef.current.seek(seekingTimeQ);
        }
      } else {
        quesVidRef.current.seek(seekingTime);
      }

      setScrubWidth({
        isDragging: true,
        width:
          scrubCircle.current.offsetLeft + state._delta[0] <= 188
            ? scrubCircle.current.offsetLeft + state._delta[0]
            : 188,
      });
    },

    {
      bounds: {
        left: 0,
        right: 188,
      },
      axis: "x",
    }
  );

  useEffect(() => {
    getResponseDeepCopy(userId, currentResponse.interviewId, token, (res) => {
      if (res.id === 200) {
        setResponseData(res.data);
      } else {
        alert(res.msg);
      }
    });

    // subscribe state change
    ansVidRef.current.subscribeToStateChange(handleStateChangeA);
    quesVidRef.current.subscribeToStateChange(handleStateChangeQ);
  }, []);

  useEffect(() => {
    if (quesVidInfo.waiting || ansVidInfo.waiting) {
      ansVidRef.current.pause();
      quesVidRef.current.pause();
    } else if (!quesVidInfo.waiting && !ansVidInfo.waiting && isPlaying) {
      if (ansVidInfo.ended || ansVidInfo.currentTime === ansVidInfo.duration) {
        setIsPlaying(false);
        return;
      }
      ansVidRef.current.play();

      if (
        quesVidInfo.ended ||
        quesVidInfo.currentTime === quesVidInfo.duration
      ) {
        ansVidRef.current.muted = false;
      } else {
        ansVidRef.current.muted = true;
        quesVidRef.current.play();
      }
    }

    if (
      responseData &&
      !quesVidInfo.waiting &&
      !ansVidInfo.waiting &&
      currentVidIndex !== responseData.questionOutList.length - 1 &&
      responseData.answerIndex &&
      !scrubWidth.isDragging
    ) {
      let ansIndexes = responseData.answerIndex.split(";");
      if (
        Math.round(ansIndexes[currentVidIndex] / 1000) ===
        Math.round(ansVidInfo.currentTime)
      ) {
        ansVidRef.current.seek(ansIndexes[currentVidIndex] / 1000);
        setCurrentVidIndex(currentVidIndex + 1);
      }
    }
  }, [ansVidInfo, quesVidInfo]);

  const handleStateChangeA = (state) => {
    if (!isNaN(state.duration) && duration === null) {
      setDuration(state.duration);
    }
    // copy player state to this component's state
    setAnsVidInfo(state);
  };
  const handleStateChangeQ = (state) => {
    // copy player state to this component's state
    setQuesVidInfo(state);
  };

  const SSTOMIN = (sec) => {
    var regx = /^[0-9.]+$/;

    if (!regx.test(sec)) {
      return "--:--";
    }

    var sec_num = parseInt(sec, 10);
    var hours = Math.floor(sec_num / 3600);
    var minutes = Math.floor((sec_num - hours * 3600) / 60);
    var seconds = sec_num - hours * 3600 - minutes * 60;

    if (minutes < 10) {
      minutes = "0" + minutes;
    }
    if (seconds < 10) {
      seconds = "0" + seconds;
    }

    return minutes + ":" + seconds;
  };

  const vidDivider = useMemo(() => {
    if (responseData && duration && responseData.answerIndex) {
      return responseData.answerIndex.split(";").map((ms, idx) => {
        let msTosec = Number(ms / 1000);
        let lft = ((msTosec / duration) * 100).toFixed(2);

        return (
          <div
            key={idx}
            className="w-[5px] h-[5px] bg-[#97AAC3] rounded-full absolute"
            style={{
              left: `${lft}%`,
              transform: `translateX(-50%)`,
              top: "140%",
            }}
          ></div>
        );
      });
    } else {
      return <></>;
    }
  }, [responseData, duration]);

  const durr = useMemo(() => {
    return SSTOMIN(duration)
      .split("")
      .map((t, idx) => (
        <span key={idx} className="w-[10px] inline-block">
          {t}
        </span>
      ));
  }, [duration]);

  const handlePlayingControl = () => {
    if (ansVidInfo.paused || !isPlaying) {
      setIsPlaying(true);
      if (ansVidInfo.currentTime === duration || ansVidInfo.ended) {
        setCurrentVidIndex(0);
      }
      ansVidRef.current.play();
      if (
        !quesVidInfo.ended &&
        quesVidInfo.currentTime <= quesVidInfo.duration
      ) {
        quesVidRef.current.play();
      }
    } else {
      setIsPlaying(false);
      ansVidRef.current.pause();
      quesVidRef.current.pause();
    }
  };

  return (
    <div className="w-[19rem] md:w-[24rem] lg:w-[29rem] rounded-[20px] flex flex-col overflow-hidden">
      <nav className="w-full flex flex-row justify-center space-x-2 px-2 items-center py-2 ">
        <button
          className="w-[1.2rem] h-[1.5rem] lg:w-[1.5rem] aspect-square rounded-full bg-[#8DACCD54] border border-[#D1E6FC] flex flex-col "
          onClick={props.back}
        >
          <IoArrowBack className="m-auto w-full text-xs lg:text-sm fill-[#2C3037]" />
        </button>
        <p className="bg-[#FFFFFF80] text-[#515A6B] mr-5 backdrop-blur-[50px] px-6 rounded-full w-fit justify-self-end text-xs py-2 font-semibold">
          Play Response
        </p>
        <span></span>
      </nav>
      {/* BODY */}
      <div className="w-full px-2 md:px-8 lg:px-[4rem] flex flex-col space-y-3 items-center pb-6 ">
        <div
          id="respond-video-record"
          className="relative flex flex-col w-[82%] rounded-full aspect-square "
        >
          <Player
            ref={ansVidRef}
            src={responseData?.ansVideoOutList[currentVidIndex]}
            className="rounded-full overflow-hidden border-[.45rem] border-[#fff] bg-black"
            width={260}
            height={260}
            fluid={false}
          >
            <ControlBar disableCompletely></ControlBar>
          </Player>

          <div
            id="respond-video-record"
            className="absolute bottom-0 right-0 rounded-full overflow-hidden border-4 border-[#fff]"
          >
            <Player
              src={responseData?.questionOutList[currentVidIndex]}
              className="rounded-full overflow-hidden m-auto"
              width={110}
              height={110}
              fluid={false}
              aspectRatio={"1:1"}
              ref={quesVidRef}
            >
              <ControlBar disableCompletely></ControlBar>
              <BigPlayButton />
            </Player>
          </div>
        </div>
        <div className="text-center w-[100%] bg-[#FFFFFFCC] text-sm rounded-[20px] py-4 px-3">
          <span className="flex flex-row w-full items-center text-sm text-[#7B94B5] mb-2 space-x-3">
            <p className="font-semibold text-[#7B94B5] mt-[2px] flex">
              {" "}
              {SSTOMIN(ansVidInfo.currentTime)
                .split("")
                .map((t, idx) => (
                  <span key={idx} className="w-[10px] inline-block">
                    {t}
                  </span>
                ))}{" "}
            </p>
            <div className="flex-grow relative">
              <div className="progressBar__container">
                <div
                  className="progressBar h-full"
                  style={{
                    width: scrubWidth.isDragging
                      ? scrubWidth.width
                      : `${(ansVidInfo.currentTime / duration) * 100 || 0}%`,
                  }}
                >
                  <button
                    className="progressBar-circle rounded-full touch-none"
                    {...bindTimelineHandler()}
                    ref={scrubCircle}
                  ></button>
                </div>
              </div>

              {vidDivider}
            </div>

            <p className="font-semibold text-[#7B94B5] mt-[2px] flex">{durr}</p>
          </span>
          <p className="text-[#2C3037] text-sm my-1">{responseData?.title}</p>
          <div className="w-full flex flex-row items-center justify-evenly text-xl">
            <span className="flex flex-row items-center space-x-1">
              <button
                className={`bg-gradient-to-b from-[#F1F9FF] to-[#DCE8F6] rounded-full p-1 ${
                  currentVidIndex === 0 ? "invisible" : ""
                }`}
                onClick={() => {
                  setCurrentVidIndex((prev) => prev - 1);
                  let ansIndexes = responseData.answerIndex.split(";");
                  let prevIndex = currentVidIndex - 2;
                  if (prevIndex < 0) {
                    ansVidRef.current.seek(0);
                  } else {
                    ansVidRef.current.seek(ansIndexes[prevIndex] / 1000);
                  }
                }}
              >
                <IoArrowBack
                  color={bg}
                  className="text-[#97AAC3] text-sm lg:text-lg"
                />
              </button>

              {!isPlaying ? (
                <button
                  className="bg-gradient-to-b from-[#F1F9FF] to-[#DCE8F6] rounded-full p-1"
                  onClick={handlePlayingControl}
                >
                  <IoPlay
                    color={bg}
                    className="text-lg lg:text-xl text-[#97AAC3]"
                  />
                </button>
              ) : (
                <button
                  className="bg-gradient-to-b from-[#F1F9FF] to-[#DCE8F6] rounded-full p-1"
                  onClick={handlePlayingControl}
                >
                  <IoStop
                    color={bg}
                    className="text-lg lg:text-xl text-[#97AAC3]"
                  />
                </button>
              )}

              <button
                className={`bg-gradient-to-b from-[#F1F9FF] to-[#DCE8F6] rounded-full p-1 ${
                  currentVidIndex === responseData?.questionOutList.length - 1
                    ? "invisible"
                    : ""
                }`}
                onClick={() => {
                  setCurrentVidIndex((prev) => prev + 1);
                  let ansIndexes = responseData.answerIndex.split(";");
                  let prevIndex = currentVidIndex;
                  ansVidRef.current.seek(ansIndexes[prevIndex] / 1000);
                }}
              >
                <IoArrowForward
                  color={bg}
                  className="text-[#97AAC3] text-sm lg:text-lg"
                />
              </button>
              <p className="text-sm text-[#515A6B] w-[54px]">
                {currentVidIndex + 1} of {responseData?.questionOutList.length}
              </p>
            </span>
          </div>
          <span className="flex flex-row justify-evenly">
            {responseData?.tag &&
              responseData?.tag.split(",").map((item, index) => (
                <div key={index} style={{ color: bg }} className="text-sm mt-2">
                  #{item}
                </div>
              ))}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PlayResponse;
